import React, { useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { InputZaverConfig } from "lib/types/generated/graphql-types"
import { useLazyQuery, useMutation } from "@apollo/client"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import Popup from "components/Ui/Popup"
import { handleErrorMessages } from "helpers/errors"
import { VALIDATE_ZAVER } from "../../../../graphql/queries/config/Providers/Zaver/ValidateZaverConfig"
import { CREATE_OR_UPDATE_ZAVER_CONFIG } from "../../../../graphql/mutations/config/Providers/Zaver/CreateOrUpdateZaverConfig"
import { ZaverConfigInputs } from "../../../../components/Config/Zaver/ZaverConfigInputs"

type Props = {
  refetch: () => void
}

interface ExtendedInputZaverConfig {
  id: string
  zaverConfig: InputZaverConfig
}

export const CreateZaverConfig = ({ refetch }: Props) => {
  const [validationError, setValidationError] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputZaverConfig>({ mode: "onBlur" })

  const [addZaverConfig, { loading: createLoading }] = useMutation(CREATE_OR_UPDATE_ZAVER_CONFIG, {
    onCompleted: () => {
      reset({
        id: "",
        zaverConfig: {
          apiKey: "",
          callbackApiKey: ""
        }
      })
      dispatch(hideEditSidebar())
      dispatch(
        alertActions.actions.createAlert({
          message: "Zaver configuration successfully created",
          type: "success"
        })
      )
      refetch()
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })

  const [validateZaver, { loading }] = useLazyQuery(VALIDATE_ZAVER, {
    onError: (error) => handleErrorMessages(error)
  })

  const onSubmit: SubmitHandler<ExtendedInputZaverConfig> = (data) => {
    const { baseUrl, apiKey, callbackApiKey } = data.zaverConfig
    validateZaver({
      variables: {
        baseUrl,
        apiKey,
        callbackApiKey
      }
    }).then((response) => {
      if (response.data.Configuration.validate.zaver.valid) {
        addZaverConfig({
          variables: {
            id: data.id,
            zaverConfig: {
              baseUrl,
              apiKey,
              callbackApiKey
            }
          }
        })
      } else {
        setValidationError(true)
      }
    })
  }

  const directSubmit: SubmitHandler<ExtendedInputZaverConfig> = (data) => {
    const { baseUrl, apiKey, callbackApiKey } = data.zaverConfig
    addZaverConfig({
      variables: {
        id: data.id,
        zaverConfig: {
          baseUrl,
          apiKey,
          callbackApiKey
        }
      }
    })
    setValidationError(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar>
        <EditSidebarHeader title="Create zaver configuration">
          <PrimaryButton type="submit" loading={createLoading || loading}>
            Save
          </PrimaryButton>
        </EditSidebarHeader>
        {validationError ? (
          <Popup
            title="Validation error"
            subtitle="Couldn't validate provided credentials with zaver. Do you want to save the zaver config anyway? "
            buttonText="Yes, save"
            handleOkClick={handleSubmit(directSubmit)}
            handleCloseClick={() => setValidationError(false)}
          />
        ) : (
          <></>
        )}
        <ZaverConfigInputs control={control} errors={errors} setValue={setValue} />
      </EditSidebar>
    </form>
  )
}
